import {
  getChargeCostTypeLabel,
  getRatingUomLabel,
  type AdditionalRateTypeValue,
  type CargoDefinition,
  type ChargeCostDefinition,
  type CustomerRateCard,
  type RegionEndpoint,
} from '@my/tms-api';
import { fmtPeriodDate, derivePeriodStatus, periodToLabel } from './period';
import {
  fuelTerritoryLabel,
  rateMethodLabel,
  type AdditionalRow,
  type FuelSurchargeRow,
  type RateEngineRule,
  type RouteOrAgreedRule,
} from './ratesChargesMock';

const ADDITIONAL_RATE_TYPE_LABEL: Record<AdditionalRateTypeValue, string> = {
  Flat: 'Flat',
  PerHour: 'Per hour',
  PercentOfBase: '% of base',
};

/**
 * How a lane end reads in the grid: the Location's name, falling back to the
 * city (route sheets created before the Locations picker have no name) and
 * then to the bare country.
 */
function endpointLabel(e: RegionEndpoint): string {
  return e.name || e.city || e.country;
}

export interface RateCardDisplay {
  engineRules: RateEngineRule[];
  routeRules: RouteOrAgreedRule[];
  fuel: FuelSurchargeRow[];
  additionals: AdditionalRow[];
}

/**
 * Project a live rate card into the grid display-row shapes. Cargo / charge
 * names are resolved from the cached org catalogues; money is formatted with
 * the party's currency symbol; status + date labels are derived from the raw
 * effective windows.
 */
export function mapRateCard(
  card: CustomerRateCard | undefined,
  cargoDefs: CargoDefinition[],
  chargeDefs: ChargeCostDefinition[],
  currencySymbol: string,
): RateCardDisplay {
  if (!card) return { engineRules: [], routeRules: [], fuel: [], additionals: [] };

  const cargoById = new Map(cargoDefs.map((d) => [d.id, d]));
  const variantName = new Map<string, string>();
  for (const d of cargoDefs) for (const v of d.variants) variantName.set(v.id, v.name);
  const chargeById = new Map(chargeDefs.map((d) => [d.id, d]));
  const money = (n: number) => `${currencySymbol}${n.toFixed(2)}`;

  const engineRules: RateEngineRule[] = card.rateSheets.map((s) => {
    const def = cargoById.get(s.cargoDefinitionId);
    const appliesTo = s.lines.map((l) =>
      l.subjectKind === 'Variant'
        ? l.cargoVariantId
          ? (variantName.get(l.cargoVariantId) ?? 'Item')
          : 'Item'
        : l.uom
          ? getRatingUomLabel(l.uom)
          : 'UOM',
    );
    const anyMultiBand = s.lines.some((l) => l.bands.length > 1);
    const firstRate = s.lines[0]?.bands[0]?.rate;
    const firstMin = s.lines[0]?.minCharge;
    return {
      id: s.id,
      autoApply: s.autoApply,
      cargoLabel: def?.displayName ?? 'Cargo',
      appliesTo: Array.from(new Set(appliesTo)),
      methodLabel: rateMethodLabel(s.rateMethod),
      rateLabel: anyMultiBand ? 'Banded' : firstRate != null ? `${money(firstRate)} / unit` : '—',
      minCharge: firstMin != null ? money(firstMin) : '—',
      fromLabel: fmtPeriodDate(s.effectiveFrom),
      toLabel: periodToLabel(s.effectiveTo),
      status: derivePeriodStatus(s.effectiveFrom, s.effectiveTo),
    };
  });

  // One display row per route line (all lines on a sheet share the lane + window).
  const routeRules: RouteOrAgreedRule[] = card.routeSheets.flatMap((s) => {
    const o = s.origin;
    const d = s.destination;
    const lane =
      o.country !== d.country
        ? `${endpointLabel(o)} (${o.country}) → ${endpointLabel(d)} (${d.country})`
        : `${endpointLabel(o)} → ${endpointLabel(d)}`;
    const fromLabel = fmtPeriodDate(s.effectiveFrom);
    const toLabel = periodToLabel(s.effectiveTo);
    const status = derivePeriodStatus(s.effectiveFrom, s.effectiveTo);
    return s.lines.map((l) => ({
      id: l.id,
      sheetId: s.id,
      sheetLineCount: s.lines.length,
      lane,
      matchMode: s.matchMode,
      autoApply: s.autoApply,
      appliesTo: [cargoById.get(l.cargoDefinitionId)?.displayName ?? 'Cargo'],
      charge: money(l.charge),
      detail: [
        getRatingUomLabel(l.uom),
        l.extraDrop != null ? `+ ${money(l.extraDrop)} / drop` : '',
      ]
        .filter(Boolean)
        .join(' · '),
      fromLabel,
      toLabel,
      status,
    }));
  });

  const fuel: FuelSurchargeRow[] = card.fuelSurcharges.map((f) => ({
    id: f.id,
    name: f.name,
    territory: fuelTerritoryLabel(f.scopeKind, f.scopeCode),
    rate: `${f.ratePercent}%`,
    fromLabel: fmtPeriodDate(f.effectiveFrom),
    toLabel: periodToLabel(f.effectiveTo),
    status: derivePeriodStatus(f.effectiveFrom, f.effectiveTo),
  }));

  const additionals: AdditionalRow[] = card.additionals.map((a) => {
    const def = chargeById.get(a.chargeCostDefinitionId);
    const rate =
      a.rateType === 'PercentOfBase'
        ? `${a.rate}%`
        : a.rateType === 'PerHour'
          ? `${money(a.rate)} / hr`
          : money(a.rate);
    return {
      id: a.id,
      charge: a.chargeDisplayName,
      sub: def ? getChargeCostTypeLabel(def.type) : '',
      rateType: ADDITIONAL_RATE_TYPE_LABEL[a.rateType],
      rate,
      fsApplicable: a.fsApplicable,
      chargeDefId: a.chargeCostDefinitionId,
    };
  });

  return { engineRules, routeRules, fuel, additionals };
}
